import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import TextField from "@mui/material/TextField";
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Select,
  MenuItem,
  Button,
} from "@mui/material";
import Pagination from "@mui/material/Pagination";
import axios from "axios";
import { FadeLoader } from "react-spinners";
import { API_BASE_URL } from "../../config/api";

// ⬇️ Ovdje mapiraš dugmad -> category ID iz WP-a
// (upisi tačne ID-eve sa WP-a)
const CATEGORY_MAP = {
  Novosti: 1,
  "Ostale novosti": null, // upiši ID ili ostavi null ako nećeš koristiti
  "Javne nabavke": 8,
  Konkursi: null, // upiši ID
};

const DEFAULT_CATEGORY_NAME = "Novosti";

const Novosti = () => {
  const [data, setData] = useState([]);
  const [filter, setFilter] = useState([]);
  const [loading, setLoading] = useState(true);

  const recordsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);

  const [selectedYear, setSelectedYear] = useState("Sve");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(DEFAULT_CATEGORY_NAME);

  const availableCategories = Object.keys(CATEGORY_MAP);

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);

        const baseCategoryId = CATEGORY_MAP[DEFAULT_CATEGORY_NAME] ?? 1;

        let allPosts = [];
        let totalPages = 1;
        let page = 1;

        while (page <= totalPages) {
          const response = await axios.get(
            `${API_BASE_URL}/posts&categories=${baseCategoryId}&_embed&_fields=id,title,date,categories,_embedded&per_page=${recordsPerPage}&page=${page}`
          );

          allPosts = allPosts.concat(response.data);
          totalPages = Number(response.headers["x-wp-totalpages"] || 1);
          page++;
        }

        allPosts.sort((a, b) => new Date(b.date) - new Date(a.date));

        setData(allPosts);
        setFilter(allPosts);
      } catch (err) {
        console.error("API error:", err);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  const availableYears = useMemo(() => {
    const years = data
      .map((post) => (post?.date ? new Date(post.date).getFullYear() : null))
      .filter(Boolean)
      .map(String);

    return [...new Set(years)].sort((a, b) => Number(b) - Number(a));
  }, [data]);

  const applyFilters = (baseData, year, query, categoryName) => {
    let out = baseData;

    // category filter: post.categories je niz ID-eva
    const catId = CATEGORY_MAP[categoryName];
    if (catId) {
      out = out.filter((post) => (post?.categories || []).includes(catId));
    }

    if (year !== "Sve") {
      out = out.filter((post) => {
        const y = post?.date ? String(new Date(post.date).getFullYear()) : "";
        return y === year;
      });
    }

    const q = (query ?? "").toLowerCase().trim();
    if (q) {
      out = out.filter((post) =>
        (post?.title?.rendered ?? "").toLowerCase().includes(q)
      );
    }

    return out;
  };

  const handleSearchChange = (val) => {
    setSearchQuery(val);
    setFilter(applyFilters(data, selectedYear, val, selectedCategory));
    setCurrentPage(1);
  };

  const handleYearFilterChange = (event) => {
    const year = event.target.value;
    setSelectedYear(year);
    setFilter(applyFilters(data, year, searchQuery, selectedCategory));
    setCurrentPage(1);
  };

  const handleCategoryFilterChange = (categoryName) => {
    setSelectedCategory(categoryName);
    setFilter(applyFilters(data, selectedYear, searchQuery, categoryName));
    setCurrentPage(1);
  };

  const nPages = Math.ceil(filter.length / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filter.slice(indexOfFirstRecord, indexOfLastRecord);

  return (
    <div className="container drugaSekcija dekanat">
      <div className="container dekanat">
        <div className="container drugaSekcija" style={{ backgroundColor: "white" }}>
          <h1>Novosti</h1>

          <div className="d-flex align-items-center mb-3" style={{ gap: 12, flexWrap: "wrap" }}>
            <TextField
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Traži novost"
            />

            <Select value={selectedYear} onChange={handleYearFilterChange} displayEmpty>
              <MenuItem value="Sve">Sve godine</MenuItem>
              {availableYears.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </div>

          {loading ? (
            <div
              className="text-center"
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "70vh",
              }}
            >
              <FadeLoader color="#123abc" loading={loading} />
            </div>
          ) : (
            <div className="d-flex bd-highlight" style={{ gap: 12 }}>
              {/* Lijevi filter buttons */}
              <div style={{ background: "white", minWidth: 220 }}>
                {availableCategories.map((category) => (
                  <Button
                    key={category}
                    variant={selectedCategory === category ? "contained" : "outlined"}
                    onClick={() => handleCategoryFilterChange(category)}
                    style={{
                      backgroundColor: selectedCategory === category ? "black" : "white",
                      color: selectedCategory === category ? "white" : "black",
                      display: "block",
                      marginBottom: 6,
                      width: "100%",
                      border: "1px solid black",
                      textTransform: "none",
                    }}
                  >
                    {category}
                  </Button>
                ))}
              </div>

              {/* Table */}
              <div className="p-2 flex-fill bd-highlight">
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell></TableCell>
                        <TableCell>Naslov</TableCell>
                        <TableCell>Datum objave</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {currentRecords.map((row) => {
                        const img =
                          row?._embedded?.["wp:featuredmedia"]?.[0]?.media_details?.sizes
                            ?.medium?.source_url ||
                          row?._embedded?.["wp:featuredmedia"]?.[0]?.source_url ||
                          "";

                        return (
                          <TableRow key={row.id}>
                            <TableCell>
                              <Link to={`/novosti/${row.id}`} className="card-link">
                                {img ? (
                                  <img src={img} alt="" height="45" />
                                ) : (
                                  <div style={{ width: 45, height: 45 }} />
                                )}
                              </Link>
                            </TableCell>

                            <TableCell>
                              <Link to={`/novosti/${row.id}`} className="card-link">
                                {row?.title?.rendered || "—"}
                              </Link>
                            </TableCell>

                            <TableCell>
                              {row?.date ? new Date(row.date).toLocaleDateString() : "—"}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {nPages > 1 && (
                  <Pagination
                    count={nPages}
                    page={currentPage}
                    onChange={(_, value) => setCurrentPage(value)}
                    style={{ marginTop: 16 }}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Novosti;
